import React__default, { useContext, memo, createElement, Fragment } from 'react';
import classnames from 'classnames';
import { Tooltip, Alert } from '@grandvision/common';
import { Icon } from '@grandvision/icons';
import { useIntl } from '@grandvision/state';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0
THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.
See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function getAvailableQuantity(availability) {
    var channels = (availability && availability.channels) || {};
    var quantity = Object.entries(channels)
        .map(function (_a) {
        var _b = __read(_a, 2), channelId = _b[0], channel = _b[1];
        return (channel && channel.availableQuantity) || 0;
    })
        .reduce(function (acc, val) { return acc + val; }, 0);
    return quantity;
}

var helpers = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getAvailableQuantity: getAvailableQuantity
});

function getDeliveryEstimation(deliveryDays, defaultEstimation) {
    if (!deliveryDays.length) {
        return defaultEstimation;
    }
    var slowest = [0, 0]; // [value, index]
    deliveryDays.forEach(function (value, index) {
        // Strip out anything except numbers and -
        var cleanValue = value.replace(/[^\d-]/g, '');
        // If we have a bound value (like 1-3), take the first number.
        var days = cleanValue.includes('-') ? Number(cleanValue.split('-')[0]) : Number(cleanValue);
        if (days > slowest[0]) {
            slowest = [days, index];
        }
    });
    // Return the string representation.
    return deliveryDays[slowest[1]];
}
function getProductDelivery(config, availability) {
    var missingDeliveryDays = config.missingInventoryChannelStrategy.expectedDeliveryDays;
    if (typeof availability === 'undefined') {
        return missingDeliveryDays;
    }
    var availableChannels = availability.channels || {};
    var channels = Object.entries(availableChannels);
    if (!channels.length) {
        return missingDeliveryDays;
    }
    // !!! Platform has no support for multiple channels yet.
    var _a = __read(channels[0], 2), channelId = _a[0], channel = _a[1];
    var channelDeliveryConfig = config.inventoryChannels[channel.key] || config.inventoryChannels[channelId];
    if (!channelDeliveryConfig) {
        console.error("Inventory channel " + (channel.key || channelId) + " is not configured.");
        return missingDeliveryDays;
    }
    if (typeof channel.availableQuantity === 'undefined') {
        return missingDeliveryDays;
    }
    var stockKey = channel.availableQuantity > 0 ? 'onStock' : 'outOfStock';
    return channelDeliveryConfig.expectedDeliveryDays[stockKey];
}
var defaultDeliveryResolver = function (config, availability) {
    if (availability === void 0) { availability = []; }
    return availability.map(function (availability) { return getProductDelivery(config, availability); });
};

var helpers$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getDeliveryEstimation: getDeliveryEstimation,
    getProductDelivery: getProductDelivery,
    defaultDeliveryResolver: defaultDeliveryResolver
});



var types = /*#__PURE__*/Object.freeze({
    __proto__: null
});

var DeliveryIndicatorContext = React__default.createContext({});
var useDeliveryIndicator = function () { return useContext(DeliveryIndicatorContext); };
function withDeliveryIndicator(Component) {
    var wrappedName = Component.displayName || Component.name || 'Component';
    var WithDeliveryIndicator = function (props) { return (React__default.createElement(DeliveryIndicatorContext.Consumer, null, function (context) { return React__default.createElement(Component, __assign({}, props, { config: context })); })); };
    WithDeliveryIndicator.displayName = "WithDeliveryIndicator(" + wrappedName + ")";
    return WithDeliveryIndicator;
}

var DeliveryIndicator = function (_a) {
    var availability = _a.availability, _b = _a.deliveryResolver, deliveryResolver = _b === void 0 ? defaultDeliveryResolver : _b, _c = _a.fallbackLabelKey, fallbackLabelKey = _c === void 0 ? 'products.deliveryIndicator.fallbackMessage' : _c, _d = _a.view, view = _d === void 0 ? 'full' : _d, className = _a.className, _e = _a["data-t"], testId = _e === void 0 ? 'delivery-indicator' : _e;
    var intl = useIntl();
    var config = useDeliveryIndicator();
    var deliveries = deliveryResolver(config, availability);
    var deliveryEstimation = getDeliveryEstimation(deliveries, config.missingInventoryChannelStrategy.expectedDeliveryDays);
    var isNextDayDelivery = deliveryEstimation === '1';
    var message = intl.getLabel(fallbackLabelKey);
    if (availability && availability.length) {
        if (config.nextDayDeliveryTimeLimit && isNextDayDelivery) {
            message = "" + intl.getFormattedLabel('products.deliveryIndicator.deliveryNextDay', {
                labelData: { timeLimit: config.nextDayDeliveryTimeLimit },
            });
        }
        else if (deliveryEstimation) {
            message = "" + intl.getFormattedLabel('products.deliveryIndicator.deliveryIndication', {
                labelData: { days: deliveryEstimation },
            });
        }
    }
    var messageElement = createElement("span", { className: "delivery-indicator__message" }, message);
    var tooltipElement = (createElement("span", { className: "delivery-indicator__tooltip" },
        createElement(Tooltip, { id: "delivery-tooltip", className: classnames("delivery-indicator__tooltip--" + view, {
                'delivery-indicator__tooltip--next-day': isNextDayDelivery,
            }), message: intl.getLabel('products.deliveryIndicator.deliveryTooltip') })));
    // view: full = Alert style
    var content = (createElement(Alert, { type: "info", icon: "delivery", "data-t": "delivery-alert" },
        createElement("span", { className: "alert__message" }, message),
        tooltipElement));
    if (view === 'short') {
        content = (createElement("span", { className: "delivery-indicator__message", "data-t": "delivery-short" }, intl.getFormattedLabel('products.deliveryIndicator.shortDeliveryIndication', {
            labelData: { days: deliveryEstimation },
        })));
    }
    if (view === 'text') {
        content = (createElement(Fragment, null,
            messageElement,
            tooltipElement));
    }
    if (view === 'icon') {
        content = (createElement(Fragment, null,
            createElement(Icon, { name: "delivery", className: "delivery-indicator__icon" }),
            messageElement,
            tooltipElement));
    }
    return (createElement("div", { "data-t": testId, className: classnames('delivery-indicator', "delivery-indicator--" + view, className, {
            'delivery-indicator__message--next-day': view === 'text' && isNextDayDelivery,
        }) }, content));
};
var DeliveryIndicator$1 = memo(DeliveryIndicator);

var DeliveryIndicatorProvider = function (_a) {
    var children = _a.children, settings = _a.settings;
    return (createElement(DeliveryIndicatorContext.Provider, { value: settings }, children));
};

var defaultStockLevels = [
    // Order matters! The `findStock()` method will walk through this array and stop at its first match
    {
        threshold: 10,
        labelId: 'inStock',
    },
    {
        threshold: 5,
        labelId: 'lowStock',
    },
    {
        threshold: 1,
        labelId: 'veryLowStock',
    },
    {
        threshold: 0,
        labelId: 'outOfStock',
    },
];
var getStockLevels = function (thresholdConfig) {
    if (thresholdConfig === void 0) { thresholdConfig = []; }
    return defaultStockLevels.map(function (level) { return (__assign(__assign({}, level), thresholdConfig.find(function (threshold) { return threshold.labelId === level.labelId; }))); });
};
var defaultStockResolver = function (allowBackOrder, stockCount, thresholdConfig) {
    if (typeof stockCount !== 'number') {
        return;
    }
    var positiveStockCount = stockCount >= 0 ? stockCount : 0; // Make sure it is >= 0
    if (!positiveStockCount && allowBackOrder) {
        return {
            threshold: 0,
            labelId: 'backOrder',
        };
    }
    return getStockLevels(thresholdConfig).find(function (stockLevel) { return positiveStockCount >= stockLevel.threshold; });
};

var helpers$2 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    defaultStockLevels: defaultStockLevels,
    getStockLevels: getStockLevels,
    defaultStockResolver: defaultStockResolver
});



var types$1 = /*#__PURE__*/Object.freeze({
    __proto__: null
});

var settingsDefaults = {
    thresholds: [],
};
var StockIndicatorContext = React__default.createContext({});
var useStockIndicator = function () { return useContext(StockIndicatorContext); };

var inventoryClassNames = {
    inStock: 'in-stock',
    lowStock: 'low-stock',
    veryLowStock: 'very-low-stock',
    outOfStock: 'out-of-stock',
    backOrder: 'back-order',
};
var StockIndicator = function (_a) {
    var stockCount = _a.stockCount, _b = _a.allowBackOrder, allowBackOrder = _b === void 0 ? true : _b, stockResolver = _a.stockResolver;
    var getLabel = useIntl().getLabel;
    var thresholds = useStockIndicator().thresholds;
    var stock = stockResolver
        ? stockResolver(allowBackOrder, stockCount, thresholds)
        : defaultStockResolver(allowBackOrder, stockCount, thresholds);
    if (!stock) {
        return null;
    }
    var labelId = stock.labelId, tooltipId = stock.tooltipId;
    var shouldShowTooltip = tooltipId || labelId === 'backOrder';
    return (createElement("div", { className: classnames('stock-indicator', "stock-indicator--level-" + inventoryClassNames[labelId]) },
        createElement("div", { className: "stock-indicator__levels" },
            createElement("span", { className: "stock-indicator__levels-level" })),
        createElement("span", { className: "stock-indicator__label" }, getLabel("products.stockInfo." + labelId)),
        shouldShowTooltip && (createElement(Tooltip, { id: "stock-indicator", className: "stock-indicator__tooltip", 
            // All stock levels can have a tooltip when configured through a custom config
            // inStockTooltip, lowStockTooltip, veryLowStockTooltip, outOfStockTooltip, backOrderTooltip
            message: getLabel("products.stockInfo." + (tooltipId || 'backOrder') + "Tooltip"), placement: "left" }))));
};

var createContext = function (settings) {
    if (settings === void 0) { settings = {}; }
    return __assign(__assign({}, settingsDefaults), settings);
};
var StockIndicatorProvider = function (_a) {
    var children = _a.children, settings = _a.settings;
    return (createElement(StockIndicatorContext.Provider, { value: createContext(settings) }, children));
};

export { DeliveryIndicator$1 as DeliveryIndicator, DeliveryIndicatorContext, helpers$1 as DeliveryIndicatorHelpers, DeliveryIndicatorProvider, types as DeliveryIndicatorTypes, helpers as Helpers, StockIndicator, StockIndicatorContext, helpers$2 as StockIndicatorHelpers, StockIndicatorProvider, types$1 as StockIndicatorTypes, useDeliveryIndicator, withDeliveryIndicator };
//# sourceMappingURL=index.es.js.map
